import { useBusinessContext } from '@root/context'
import {
  fetchInvoice,
  fetchPurchaseInvoice,
  IInvoice,
  IPurchaseInvoice
} from '@root/query/invoices'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'

export const invoiceQueryKey = (slug: string, invoiceId: number) =>
  [slug, 'invoices', invoiceId] as const

export const purchaseInvoiceQueryKey = (slug: string, purchaseInvoiceId: number) =>
  [slug, 'purchase_invoices', purchaseInvoiceId] as const

export const invoiceQueryOptions = ({ slug, invoiceId }: { slug: string; invoiceId: number }) => ({
  queryKey: invoiceQueryKey(slug, invoiceId),
  queryFn: () => fetchInvoice({ slug, invoiceId })
})

export const purchaseInvoiceQueryOptions = ({
  slug,
  purchaseInvoiceId
}: {
  slug: string
  purchaseInvoiceId: number
}) => ({
  queryKey: purchaseInvoiceQueryKey(slug, purchaseInvoiceId),
  queryFn: () => fetchPurchaseInvoice({ slug, purchaseInvoiceId })
})

export const useInvoice = (
  { invoiceId }: { invoiceId: number | undefined },
  options: Omit<UseQueryOptions<IInvoice>, 'queryKey' | 'queryFn'> = {}
) => {
  const { slug } = useBusinessContext()
  const { enabled = true, ...rest } = options
  return useQuery({
    ...invoiceQueryOptions({ slug, invoiceId: invoiceId ?? 0 }),
    ...rest,
    enabled: Boolean(slug && invoiceId) && enabled
  })
}

export const usePurchaseInvoice = (
  { purchaseInvoiceId }: { purchaseInvoiceId: number | undefined },
  options: Omit<UseQueryOptions<IPurchaseInvoice>, 'queryKey' | 'queryFn'> = {}
) => {
  const { slug } = useBusinessContext()
  const { enabled = true, ...rest } = options
  return useQuery({
    ...purchaseInvoiceQueryOptions({ slug, purchaseInvoiceId: purchaseInvoiceId ?? 0 }),
    ...rest,
    enabled: Boolean(slug && purchaseInvoiceId) && enabled
  })
}
